import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDefaultWorkspace } from "@/hooks/useDefaultWorkspace";
import {
  getActiveNavItem,
  getMobileNavItemsV2,
  getSidebarNavItemByHref,
  getSidebarNavItemsV2,
  isNavSection,
  type NavItem,
} from "./nav-items";

/** Hrefs surfaced as quick-access buttons in the bottom bar, in display order. */
const QUICK_NAV_HREFS = ["/explore", "/static-ads", "/video-ads", "/projects"] as const;

const QUICK_NAV_LABELS: Record<(typeof QUICK_NAV_HREFS)[number], string> = {
  "/explore": "Home",
  "/static-ads": "Images",
  "/video-ads": "Videos",
  "/projects": "Projects",
};

/**
 * Mobile bottom nav — quick-access links for the most common destinations
 * plus a Menu button that opens a side drawer with the full sidebar.
 *
 * Drawer closes on every nav-link click so users immediately see the page
 * they navigated to.
 */
export function MobileBottomNav() {
  const { pathname } = useLocation();
  const { defaultWorkspaceSlug } = useDefaultWorkspace();
  const [open, setOpen] = useState(false);

  // Close drawer whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const navItems = getMobileNavItemsV2(defaultWorkspaceSlug);
  const activeItem = getActiveNavItem(pathname, navItems);
  const entries = getSidebarNavItemsV2(defaultWorkspaceSlug);

  const quickItems = QUICK_NAV_HREFS.map((href) =>
    getSidebarNavItemByHref(href, defaultWorkspaceSlug),
  ).filter((item): item is NavItem => item !== null);

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden flex h-16 shrink-0 items-stretch bg-card/95 backdrop-blur-[16px] shadow-ambient"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {quickItems.map((item) => {
          const isActive = activeItem === item;
          const label = QUICK_NAV_LABELS[item.href as (typeof QUICK_NAV_HREFS)[number]] ?? item.name;
          return (
            <Link
              key={item.href}
              to={item.href}
              aria-label={item.name}
              aria-current={isActive ? "page" : undefined}
              data-testid={`bottom-nav-${item.href.replace(/^\//, "").replace(/\//g, "-")}`}
              data-highlight-id={item.highlightId}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 px-1 py-2 transition-colors touch-manipulation",
                isActive ? "text-primary" : "text-foreground/70 hover:text-foreground",
              )}
            >
              <item.icon className="h-6 w-6" />
              <span className="ds-micro truncate max-w-full">
                {label}
              </span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open navigation"
          aria-expanded={open}
          data-testid="bottom-nav-hamburger"
          className="flex flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-foreground/70 hover:text-foreground transition-colors touch-manipulation"
        >
          <Menu className="h-6 w-6" />
          <span className="ds-micro">Menu</span>
        </button>
      </nav>

      {/* Backdrop + drawer panel — md:hidden so desktop never sees it. */}
      <button
        type="button"
        aria-label="Close navigation"
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
        onClick={() => setOpen(false)}
        className={cn(
          "md:hidden fixed inset-0 z-50 cursor-default bg-foreground/40 backdrop-blur-sm transition-opacity",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        )}
      />

      <aside
        aria-hidden={!open}
        data-testid="mobile-nav-drawer"
        className={cn(
          "md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[80vw] bg-surface-container border-r border-border/50 shadow-ambient",
          "flex flex-col py-4 transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between px-4 mb-4">
          <span className="ds-title">
            Wiser<span className="text-primary">Studio</span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close navigation"
            className="flex items-center justify-center h-8 w-8 rounded-md text-foreground/60 hover:bg-accent hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-1 px-2 flex-1 overflow-y-auto ds-scrollbar">
          {entries.map((entry, idx) => {
            if (isNavSection(entry)) {
              return (
                <div
                  key={`section-${idx}-${entry.header}`}
                  className="px-3 pt-3 pb-1 ds-micro-strong uppercase text-foreground/40"
                >
                  {entry.header}
                </div>
              );
            }
            const isActive = activeItem === entry;
            return (
              <Link
                key={entry.href}
                to={entry.href}
                onClick={() => setOpen(false)}
                data-highlight-id={entry.highlightId}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-xl ds-body-sm-strong transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/70 hover:bg-accent hover:text-foreground",
                )}
              >
                <entry.icon className="h-[18px] w-[18px] shrink-0" />
                <span className="truncate">{entry.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
